import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, authedFileUrl, API_BASE } from "../lib/api";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Bell, ImageIcon, FileText, ArrowRight, Loader2, CalendarDays, MapPin, Clock, CalendarPlus } from "lucide-react";
import { format, parseISO, isToday, isTomorrow } from "date-fns";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";

export default function ParentDashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/parent/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
    }, []);

    if (loading) return <CenterSpinner />;

    return (
        <div className="space-y-8" data-testid="parent-dashboard">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Parent Portal</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-1">
                    Welcome back, <span className="text-secondary-foreground">{user?.name?.split(" ")[0] || "Parent"}</span>
                </h1>
                <p className="text-muted-foreground mt-2">Here&apos;s what&apos;s new at Childhood today.</p>
            </motion.div>

            {/* Quick links */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <QuickLink to="/parent/notifications" icon={Bell} label="Notifications" count={data?.recent_notifications?.length} testId="quick-notifications" />
                <QuickLink to="/parent/events" icon={CalendarDays} label="Events" count={data?.upcoming_events?.length} testId="quick-events" />
                <QuickLink to="/parent/photos" icon={ImageIcon} label="Photo Albums" count={data?.recent_photos?.length} testId="quick-photos" />
                <QuickLink to="/parent/documents" icon={FileText} label="Documents" count={data?.recent_documents?.length} testId="quick-documents" />
            </div>

            {/* Upcoming events */}
            {data?.upcoming_events?.length > 0 && (
                <section>
                    <SectionTitle title="Upcoming Events" link="/parent/events" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {data.upcoming_events.slice(0, 4).map((ev) => {
                            const d = parseISO(ev.event_date);
                            const badge = isToday(d) ? "Today" : isTomorrow(d) ? "Tomorrow" : null;
                            const icsHref = `${API_BASE}/events/${ev.id}/ics?token=${encodeURIComponent(localStorage.getItem("ccc_token") || "")}`;
                            return (
                                <div key={ev.id} className="flex items-center gap-3 bg-card border border-border rounded-2xl p-4">
                                    <div className="shrink-0 w-14 h-14 rounded-2xl bg-primary/20 flex flex-col items-center justify-center">
                                        <div className="text-[10px] font-bold uppercase tracking-widest text-primary-foreground">{format(d, "MMM")}</div>
                                        <div className="text-xl font-black font-heading text-primary-foreground leading-none">{format(d, "d")}</div>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="font-bold font-heading truncate">{ev.title}</div>
                                            {badge && <span className="text-[10px] uppercase tracking-wider bg-secondary text-secondary-foreground rounded-full px-2 py-0.5 font-bold">{badge}</span>}
                                        </div>
                                        <div className="text-xs text-muted-foreground truncate">
                                            {format(d, "EEE, d MMM")}{ev.event_time ? ` · ${ev.event_time}` : ""}{ev.location ? ` · ${ev.location}` : ""}
                                        </div>
                                    </div>
                                    <Button asChild size="sm" variant="ghost" className="rounded-full shrink-0" data-testid={`dashboard-add-to-calendar-${ev.id}`} aria-label="Add to calendar">
                                        <a href={icsHref} download>
                                            <CalendarPlus className="h-4 w-4" />
                                        </a>
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Recent notifications */}
            <section>
                <SectionTitle title="Recent Notifications" link="/parent/notifications" />
                <div className="space-y-3">
                    {data?.recent_notifications?.length === 0 && <EmptyState label="No notifications yet" />}
                    {data?.recent_notifications?.map((n) => (
                        <Card key={n.id} className="rounded-2xl">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-lg font-heading">{n.title}</CardTitle>
                                <CardDescription>{new Date(n.created_at).toLocaleString()}</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="notif-prose text-sm line-clamp-3" dangerouslySetInnerHTML={{ __html: n.content }} />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </section>

            {/* Recent photos */}
            <section>
                <SectionTitle title="Recent Photos" link="/parent/photos" />
                {data?.recent_photos?.length === 0 ? (
                    <EmptyState label="No photos uploaded yet" />
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                        {data?.recent_photos?.map((p) => (
                            <div key={p.id} className="aspect-square rounded-2xl overflow-hidden bg-muted">
                                <img src={authedFileUrl(p.image_url)} alt={p.caption || "Photo"} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

const QuickLink = ({ to, icon: Icon, label, count, testId }) => (
    <Link
        to={to}
        data-testid={testId}
        className="group flex items-center justify-between bg-card border border-border rounded-2xl p-5 hover:-translate-y-1 hover:shadow-md transition-all"
    >
        <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-primary/20 flex items-center justify-center">
                <Icon className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
                <div className="font-semibold">{label}</div>
                <div className="text-xs text-muted-foreground">{count ?? 0} recent</div>
            </div>
        </div>
        <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
    </Link>
);

const SectionTitle = ({ title, link }) => (
    <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-xl font-bold">{title}</h2>
        {link && (
            <Link to={link} className="text-sm text-secondary-foreground hover:underline">
                See all
            </Link>
        )}
    </div>
);

const EmptyState = ({ label }) => (
    <div className="text-center text-muted-foreground py-10 border border-dashed border-border rounded-2xl text-sm">{label}</div>
);

const CenterSpinner = () => (
    <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
);
